import React, { useId, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import { Action, cardSx, Chapter, colors, Eyebrow, fonts, Frame, homeContactLabel, PortalView, splitSx } from './design';
import { HomeClosingSections } from './HomeClosingSections';
import { TeaserImage } from './TeaserImage';
import { TeaserRecommendations } from './TeaserRecommendations';
import { buildingTeaserObservations, buildingTeaserSearch } from './buildingTeaserData';
import { PublicLocale, publicPath } from './routes';

const headingSx = { fontSize: { xs: '2rem', md: '2.65rem' }, maxWidth: 700 };
const copy = {
  sv: {
    eyebrow: 'Fasadinspektion för fastighetsägare',
    title: ['Ge byggnaden ett minne.', 'Se vad som behöver göras nu.'],
    lead: 'Få ett tydligare underlag för nästa beslut om din fastighet. Vi samlar bilder, prioriterade observationer och förslag på nästa steg – så att du kan ta frågorna vidare med rätt kompetens.',
    technology: 'Drönarinspektion · AI-stöd · Ingenjörsbedömning med Expert',
    scope: 'AI-genererade prioriterade observationer och förslag på nästa steg, granskade av KlaraView-teamet.',
    example: 'Se ett exempel',
    nextLabel: 'NÄSTA STEG / EXEMPEL',
    next: 'En markerad spricka behöver bedömas. Låt en byggnadsingenjör undersöka detaljen innan en åtgärd beslutas.',
    questionsLabel: '01 / TRE FRÅGOR',
    questionsTitle: 'Vad behöver en närmare titt?',
    questions: [
      ['Hitta', 'Vad ser vi?', 'Få syn på observationer i drönarbilderna, med bild och plats som sammanhang.'],
      ['Prioritera', 'Vad ska vi titta på först?', 'Ta vidare granskade prioriteringar och föreslagna nästa steg. De är inte en verifierad riskbedömning.'],
      ['Följ upp', 'Vad behöver dokumenteras igen?', 'Använd tidigare material som sammanhang när nästa inspektion planeras.'],
    ],
    timeLabel: '02 / BYGGNADSPASSET',
    timeTitle: 'Byggnadens minne börjar här.',
    intelligence: 'Building Intelligence – från bilder till byggnadskunskap.',
    timeLead: 'I Byggnadspasset hålls bilder, observationer och rapporter samman per fastighet. Ett underlag att bygga vidare på, inte bara en enskild leverans.',
    modelAlt: '3D-modell av hörnhuset',
    modelCaption: '3D-modell med markerat läge för observationen.',
    photoCaption: 'Befintligt portalfynd',
    previewNote: 'Markeringen på modellen och i närbilden visar samma observation. Öppna Byggnadspasset för att utforska fler fynd.',
    stagesLabel: 'Från första inspektion till uppföljning',
    stages: [
      ['Första inspektionen', 'Ett underlag att börja med.', 'Bilder och observationer samlas per fastighet. Scan ger AI-prioriteringar och förslag på nästa steg, granskade av KlaraView-teamet. Med Expert ingår fördjupad undersökning av byggnadsingenjör. Beroende på frågeställning och avtalad omfattning kan även undersökningar på plats ingå.'],
      ['Uppföljning', 'Tidigare material ger sammanhang.', 'Tidigare bilder och observationer kan ge sammanhang vid nästa inspektion. Jämförbarheten beror på bildmaterialet. Uppföljande dokumentation avtalas separat – ingen automatisk matchning eller verifierad förändringsanalys ingår.'],
      ['Efter åtgärd', 'Dokumentera det som har gjorts.', 'Dokumentera status och kommentarer vid ett fynd i rapporten. Exportera rapporten som PDF eller dela en läslänk. En registrerad status som ”Åtgärdad” verifierar inte reparationen.'],
    ],
    timeNote: 'Illustrativt arbetsflöde, inte en verklig kunds åtgärds- eller inspektionshistorik.',
    buildingPass: 'Utforska byggnadspasset',
    portfolioLabel: '03 / HELA BESTÅNDET',
    portfolioTitle: 'Vilka fastigheter behöver din uppmärksamhet först?',
    portfolioLead: 'Gå från överblick över beståndet till bilder och observationer i en fastighet. Samlad dokumentation gör det lättare att ta nästa fråga vidare och planera var en närmare bedömning behövs.',
    managers: 'För dig som förvaltar flera fastigheter',
    services: 'Se vad som ingår i tjänsterna',
    reviewLabel: '04 / FRÅN OBSERVATION TILL BEDÖMNING',
    reviewTitle: 'En markering är början på en fråga.',
    reviewLead: 'Bilden, platsen och nästa steg hör ihop. Börja med granskade observationer och ta frågan vidare med rätt kompetens.',
    serviceScope: [
      ['Scan', 'Bilddokumentation, AI-genererade prioriterade observationer och förslag på nästa steg, granskade av KlaraView-teamet. Det är inte en ingenjörsbedömning.'],
      ['Expert', 'Oberoende ingenjörsgranskning inom avtalad omfattning. Strukturerat tekniskt underlag inför projektering och upphandling – inte komplett förfrågningsunderlag eller verifiering av utförda reparationer.'],
    ],
    caution: 'En markering är inte en diagnos.',
    cautionBody: 'Orsak, omfattning och lämplig åtgärd behöver bedömas med rätt kompetens och tillräckligt underlag.',
    technologyLink: 'Vem bedömer vad?',
  },
  en: {
    eyebrow: 'Façade inspection for property owners',
    title: ['Give your building a memory.', 'See what needs doing now.'],
    lead: 'Get a clearer basis for your next property decision. We bring together images, prioritised observations and suggested next steps – so you can take the questions forward with the right expertise.',
    technology: 'Drone inspection · AI support · Engineering assessment with Expert',
    scope: 'AI-generated prioritised observations and suggested next steps, reviewed by the KlaraView team.',
    example: 'See an example',
    nextLabel: 'NEXT STEP / EXAMPLE',
    next: 'A marked crack needs assessment. Ask a building engineer to examine the detail before deciding on a repair.',
    questionsLabel: '01 / THREE QUESTIONS',
    questionsTitle: 'What needs a closer look?',
    questions: [
      ['Find', 'What do we see?', 'See observations in drone imagery, with the image and location providing context.'],
      ['Prioritise', 'What should we look at first?', 'Take reviewed priorities and suggested next steps forward. They are not a verified risk assessment.'],
      ['Follow up', 'What needs documenting again?', 'Use earlier material as context when planning the next inspection.'],
    ],
    timeLabel: '02 / THE BUILDING PASS',
    timeTitle: "Your building's memory starts here.",
    intelligence: 'Building Intelligence — from images to knowledge about the building.',
    timeLead: 'The Building pass keeps images, observations and reports together by property. A record to build on, rather than just a single delivery.',
    modelAlt: '3D model of the corner building',
    modelCaption: '3D model showing the marked location of the observation.',
    photoCaption: 'Existing portal finding',
    previewNote: 'The markers on the model and in the close-up show the same observation. Open the Building pass to explore more findings.',
    stagesLabel: 'From first inspection to follow-up',
    stages: [
      ['First inspection', 'A starting point for decisions.', 'Images and observations are collected by property. Scan provides AI priorities and suggested next steps, reviewed by the KlaraView team. Expert includes a detailed investigation by a building engineer. Depending on the question being investigated and the agreed scope, on-site investigations may also be included.'],
      ['Follow-up', 'Earlier material provides context.', 'Earlier images and observations can provide context at the next inspection. Comparability depends on the imagery. Follow-up documentation is agreed separately – automatic matching and verified change analysis are not included.'],
      ['After repairs', 'Record what has been done.', 'Record status and comments against a finding in the report. Export the report as PDF or share a read-only link. A recorded status of “Fixed” does not verify the repair.'],
    ],
    timeNote: "Illustrative workflow, not an actual customer's repair or inspection history.",
    buildingPass: 'Explore the building pass',
    portfolioLabel: '03 / THE WHOLE PORTFOLIO',
    portfolioTitle: 'Which properties need your attention first?',
    portfolioLead: 'Move from a portfolio overview to images and observations for one property. Connected documentation helps you take the next question forward and plan where a closer assessment is needed.',
    managers: 'For those managing several properties',
    services: 'See what the services include',
    reviewLabel: '04 / FROM OBSERVATION TO ASSESSMENT',
    reviewTitle: 'A marker is the start of a question.',
    reviewLead: 'The image, location and next step belong together. Start with reviewed observations and take the question forward with the right expertise.',
    serviceScope: [
      ['Scan', 'Image documentation, AI-generated prioritised observations and suggested next steps, reviewed by the KlaraView team. This is not an engineering assessment.'],
      ['Expert', 'Independent engineering review within the agreed scope. Structured technical documentation to support design and procurement — not complete tender documentation or verification of completed repairs.'],
    ],
    caution: 'A marker is not a diagnosis.',
    cautionBody: 'The cause, extent and appropriate action need assessment with the right expertise and sufficient evidence.',
    technologyLink: 'Who assesses what?',
  },
};

export function HomePage({ locale }: { locale: PublicLocale; search?: string }) {
  const text = copy[locale];
  const finding = buildingTeaserObservations(locale)[0];
  const [stage, setStage] = useState(0);
  const [detailedModel, setDetailedModel] = useState(false);
  const id = useId();
  return <>
    <Box component="section" sx={{ pt: { xs: 5, md: 6 }, pb: { xs: 5, md: 7 } }}>
      <Frame><Box sx={{ ...splitSx, gap: { xs: 5, md: 6 }, gridTemplateColumns: { xs: '1fr', md: '1.04fr 1fr' } }}>
        <Box><Eyebrow>{text.eyebrow}</Eyebrow>
          <Typography variant="h1" sx={{ fontSize: { xs: 'clamp(2rem, 8.5vw, 3.8rem)', md: 'clamp(3rem, 4vw, 3.65rem)' }, mb: 3 }}>
            {text.title[0]}{' '}<Box component="span" sx={{ color: colors.rust }}>{text.title[1]}</Box>
          </Typography>
          <Typography sx={{ color: colors.muted, maxWidth: 510, fontSize: 17 }}>{text.lead}</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', my: 3 }}>
            <Action href={publicPath('contact', locale)}>{homeContactLabel[locale]}</Action>
            <Action href={`${publicPath('home', locale)}#prova`} secondary>{text.example}</Action>
          </Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1.5 }}>{text.technology}</Typography>
          <Typography sx={{ fontSize: 13, color: colors.muted, maxWidth: 510 }}>{text.scope}</Typography>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <PortalView image="portalHero" locale={locale} eager sizes="(min-width: 1320px) 590px, (min-width: 900px) 46vw, calc(100vw - 40px)" />
          <Box sx={{ borderLeft: `2px solid ${colors.rust}`, p: 2, mt: 2, bgcolor: colors.white }}>
            <Typography sx={{ fontFamily: fonts.mono, fontSize: 11, mb: 1 }}>{text.nextLabel}</Typography>
            <Typography sx={{ fontSize: 14 }}>{text.next}</Typography>
          </Box>
        </Box>
      </Box></Frame>
    </Box>
    <Chapter id="kundcase" tone="soft">
      <Eyebrow>{text.questionsLabel}</Eyebrow>
      <Typography variant="h2" sx={headingSx}>{text.questionsTitle}</Typography>
      <Box component="ul" aria-label={text.questionsTitle} sx={{ listStyle: 'none', p: 0, my: 4, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
        {text.questions.map(([title, question, body]) => <Box component="li" key={title} sx={cardSx}>
          <Typography variant="h3" sx={{ fontSize: 25, mb: 2 }}>{title}</Typography>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>{question}</Typography>
          <Typography sx={{ fontSize: 15, color: colors.muted }}>{body}</Typography>
        </Box>)}
      </Box>
    </Chapter>
    <Chapter id="over-tid">
      <Box id="prova" sx={{ scrollMarginTop: 110 }}>
        <Eyebrow>{text.timeLabel}</Eyebrow>
        <Box sx={{ ...splitSx, mb: 4 }}>
          <Typography variant="h2" sx={headingSx}>{text.timeTitle}</Typography>
          <Box>
            <Typography sx={{ fontWeight: 700, mb: 2 }}>{text.intelligence}</Typography>
            <Typography sx={{ color: colors.muted }}>{text.timeLead}</Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ ...splitSx, gap: { xs: 3, md: 4 }, alignItems: 'start' }}>
        <Box component="figure" sx={{ m: 0, minWidth: 0 }}>
          <Box sx={{ position: 'relative' }}>
            <TeaserImage image="building" alt={text.modelAlt} locale={locale} finding={finding} detailedModel={detailedModel} />
            <IconButton aria-label={locale === 'sv' ? 'Detaljerad 3D-modell' : 'Detailed 3D model'}
              aria-pressed={detailedModel} onClick={() => setDetailedModel(value => !value)}
              sx={{ position: 'absolute', right: 12, top: 12, width: 44, height: 44,
                bgcolor: detailedModel ? colors.ink : colors.white, color: detailedModel ? colors.white : colors.ink,
                border: `1px solid ${colors.line}`,
                '&:hover': { bgcolor: detailedModel ? colors.graphite : colors.soft } }}>
              <ViewInArIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography component="figcaption" sx={{ mt: 1.5, fontSize: 12, color: colors.muted }}>{text.modelCaption}</Typography>
        </Box>
        <Box component="figure" sx={{ m: 0, minWidth: 0 }}>
          <TeaserImage image={finding.photo} alt={finding.photoAlt} locale={locale} finding={finding} />
          <Typography component="figcaption" sx={{ mt: 1.5, fontSize: 14 }}>
            {text.photoCaption} · {finding.id} / {finding.title}
          </Typography>
          <Box sx={{ mt: 2 }}><Action href={`${publicPath('buildingPass', locale)}${buildingTeaserSearch(finding.id)}`} quiet>{text.buildingPass}</Action></Box>
        </Box>
      </Box>
      <Typography sx={{ fontSize: 12, color: colors.muted, mt: 3, mb: 4 }}>{text.previewNote}</Typography>
      <Box role="group" aria-label={text.stagesLabel} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1 }}>
        {text.stages.map(([label], index) => <Button key={label} aria-pressed={stage === index} aria-controls={`${id}-stage`}
          onClick={() => setStage(index)} sx={{ justifyContent: 'flex-start', gap: 2, p: 2, color: stage === index ? colors.white : colors.ink,
            bgcolor: stage === index ? colors.graphite : colors.soft, '&:hover': { bgcolor: stage === index ? colors.graphite : colors.line } }}>
          <Box component="span" aria-hidden="true" sx={{ fontFamily: fonts.mono, color: stage === index ? colors.copper : colors.muted }}>0{index + 1}</Box>
          {label}
        </Button>)}
      </Box>
      <Box id={`${id}-stage`} role="region" aria-label={text.stagesLabel} aria-live="polite" aria-atomic="true"
        sx={{ p: { xs: 3, md: 4 }, border: `1px solid ${colors.line}`, borderTop: `3px solid ${colors.rust}`, minHeight: { md: 180 } }}>
        <Typography variant="h3" sx={{ fontSize: 25, mb: 2 }}>{text.stages[stage][1]}</Typography>
        <Typography sx={{ maxWidth: 880, color: colors.muted }}>{text.stages[stage][2]}</Typography>
      </Box>
      <Typography sx={{ fontSize: 12, color: colors.muted, mt: 2 }}>{text.timeNote}</Typography>
    </Chapter>
    <Chapter dark>
      <Eyebrow dark>{text.portfolioLabel}</Eyebrow>
      <Typography variant="h2" sx={headingSx}>{text.portfolioTitle}</Typography>
      <Typography sx={{ maxWidth: 760, mt: 3, mb: 5, color: '#CFD5CC' }}>{text.portfolioLead}</Typography>
      <PortalView image="portalPortfolio" locale={locale} dark />
      <Box sx={{ mt: 4 }}><Action href={publicPath('forManagers', locale)} quiet>{text.managers}</Action></Box>
    </Chapter>
    <Chapter>
      <Eyebrow>{text.reviewLabel}</Eyebrow>
      <Typography variant="h2" sx={headingSx}>{text.reviewTitle}</Typography>
      <Typography sx={{ maxWidth: 760, mt: 3, mb: 5, color: colors.muted }}>{text.reviewLead}</Typography>
      <Typography variant="h3" sx={{ fontSize: 25, mb: 3 }}>{finding.id} / {finding.title}</Typography>
      <Box sx={{ ...splitSx, gap: { xs: 3, md: 4 }, alignItems: 'start' }}>
        <Box component="figure" sx={{ m: 0, minWidth: 0 }}>
          <TeaserImage image={finding.photo} alt={finding.photoAlt} locale={locale} finding={finding} />
          <Typography component="figcaption" sx={{ mt: 1.5, fontSize: 12, color: colors.muted }}>
            {text.photoCaption} · {finding.id}
          </Typography>
        </Box>
        <TeaserRecommendations finding={finding} locale={locale} showContact={false} />
      </Box>
      <Box sx={{ ...splitSx, alignItems: 'start', my: 4, gap: 3 }}>
        {text.serviceScope.map(([title, body]) => <Box key={title} sx={{ borderTop: `2px solid ${colors.rust}`, pt: 3 }}>
          <Typography variant="h3" sx={{ fontSize: 25, mb: 2 }}>{title}</Typography>
          <Typography sx={{ color: colors.muted, fontSize: 15 }}>{body}</Typography>
        </Box>)}
      </Box>
      <Box sx={{ ...splitSx, bgcolor: colors.soft, p: 3 }}>
        <Box><Typography sx={{ fontWeight: 700, mb: 1 }}>{text.caution}</Typography>
          <Typography sx={{ color: colors.muted, fontSize: 15 }}>{text.cautionBody}</Typography></Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifySelf: { md: 'end' }, gap: 1 }}>
          <Action href={publicPath('services', locale)} quiet>{text.services}</Action>
          <Action href={publicPath('technology', locale)} quiet>{text.technologyLink}</Action>
        </Box>
      </Box>
    </Chapter>
    <HomeClosingSections locale={locale} />
  </>;
}
